import { Outlet, RouterProvider, createBrowserRouter } from "react-router-dom"
import AuthenticationScreen from "./authentication/authentication.screen"
import DashboardScreen from "./dashboard/dashboard.screen"
import { useDynamicContext } from "@dynamic-labs/sdk-react-core"
import AppFrame from "./app-frame/app-frame"
import SettingsScreen from "./settings/settings.screen"
import PaymentLinkCreateScreen from "./payment-links/payment-link-create.screen"
import PaymentLinkViewScreen from "./payment-links/payment-link-view.screen"
import PaymentLinksListScreen from "./payment-links/payment-links-list.screen"
import PaymentsHomeScreen from "./payments/payments-home.screen"
import BalancesHomeScreen from "./balances/balances-home.screen"
import NotFoundScreen from "./not-found.screen"
import AccountScreen from "./settings/account.screen"
import CustomersHomeScreen from "./customers/customers-home.screen"
import CustomerViewScreen from "./customers/customer-view.screen"
import InvoicesHomeScreen from "./invoices/invoices-home.screen"
import InvoiceEditScreen from "./invoices/invoice-edit.screen"
import InvoiceCreateScreen from "./invoices/invoice-create.screen"
import InvoiceViewScreen from "./invoices/invoice-view.screen"
import UserIsReady from "./onboarding/user-is-ready"
import OnboardingScreen from "./onboarding/onboarding.screen"
import * as Sentry from "@sentry/react"
import ErrorScreen from "./error.screen"
import WalletScreen from "./settings/wallet.screen"
import UserSetup from "./authentication/user-setup"
import BankAccountsScreen from "./settings/bank-accounts/bank-accounts.screen"
import TransactionsScreen from "./transactions/transactions.screen"
import WebhookScreen from "./settings/webhook/webhook.screen"
import ApiKeysScreen from "./settings/api-keys/api-keys.screen"
import KycScreen from "./kyc/kyc.screen"

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter)

const guestRoutes = sentryCreateBrowserRouter([
  {
    path: "/*",
    element: (
      <Sentry.ErrorBoundary fallback={<ErrorScreen />}>
        <AuthenticationScreen />
      </Sentry.ErrorBoundary>
    ),
  },
])

const userRoutes = sentryCreateBrowserRouter([
  {
    element: (
      <Sentry.ErrorBoundary fallback={<ErrorScreen />}>
        <UserSetup>
          <Outlet />
        </UserSetup>
      </Sentry.ErrorBoundary>
    ),
    children: [
      {
        path: "/",
        element: <AppFrame />,
        children: [
          {
            path: "/",
            element: <DashboardScreen />,
          },
          {
            path: "/settings",
            element: <SettingsScreen />,
          },
          {
            path: "/payment-links/:id",
            element: <PaymentLinkViewScreen />,
          },
          {
            path: "/payment-links",
            element: <PaymentLinksListScreen />,
          },
          {
            path: "/payments/:paymentId?",
            element: <PaymentsHomeScreen />,
          },
          {
            path: "/balances/:withdrawalId?",
            element: <BalancesHomeScreen />,
          },
          {
            path: "/settings/account",
            element: <AccountScreen />,
          },
          {
            path: "/customers",
            element: <CustomersHomeScreen />,
          },
          {
            path: "/customers/:id/:mode?",
            element: <CustomerViewScreen />,
          },
          {
            path: "/invoices",
            element: <InvoicesHomeScreen />,
          },
          {
            path: "/invoices/:id",
            element: <InvoiceViewScreen />,
          },
          {
            path: "/settings/wallet",
            element: <WalletScreen />,
          },
          {
            path: "/settings/bank-accounts/:bankAccountId?",
            element: <BankAccountsScreen />,
          },
          {
            path: "/transactions",
            element: <TransactionsScreen />,
          },
          {
            path: "/settings/webhook",
            element: <WebhookScreen />,
          },
          {
            path: "/settings/api-keys",
            element: <ApiKeysScreen />,
          },
          {
            path: "*",
            element: <NotFoundScreen />,
          },
        ],
      },
      {
        path: "/onboarding/:action?",
        element: <OnboardingScreen />,
      },
      {
        path: "/kyc",
        element: <KycScreen />,
      },
      {
        path: "/payment-links/create",
        element: (
          <UserIsReady>
            <PaymentLinkCreateScreen />
          </UserIsReady>
        ),
      },
      {
        path: "/invoices/:id/edit",
        element: <InvoiceEditScreen />,
      },
      {
        path: "/invoices/create",
        element: (
          <UserIsReady>
            <InvoiceCreateScreen />
          </UserIsReady>
        ),
      },
    ],
  },
])

export default function Routes() {
  const { isAuthenticated } = useDynamicContext()

  return <RouterProvider router={isAuthenticated ? userRoutes : guestRoutes} />
}
